/**
 * URL utilities for handling file paths and avatar URLs across services
 */

import { getServiceUrl, isLocalhost } from './serviceUrls.js';

/**
 * Normalize an avatar URL to a full URL that can be accessed from any service
 * 
 * - If the URL is already absolute (starts with http/https), return as-is
 * - If the URL is relative (starts with /), prepend the auth service URL
 * - If the URL is empty/null, return empty string
 * 
 * @param avatarUrl - The avatar URL from the API (can be relative or absolute)
 * @returns A full URL that points to the auth service
 * 
 * @example
 * ```ts
 * normalizeAvatarUrl('/files/profiles/profile-123.jpg')
 * // Returns 'http://localhost:4000/files/profiles/profile-123.jpg' (in dev)
 * // Returns 'https://auth.airclou.com/files/profiles/profile-123.jpg' (in prod)
 * 
 * normalizeAvatarUrl('https://auth.airclou.com/files/profiles/profile-123.jpg')
 * // Returns 'https://auth.airclou.com/files/profiles/profile-123.jpg' (as-is)
 * 
 * normalizeAvatarUrl('')
 * // Returns ''
 * ```
 */
export function normalizeAvatarUrl(avatarUrl: string | null | undefined): string {
  if (!avatarUrl) return '';

  // Absolute URL handling with environment-aware rewriting
  if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
    try {
      const u = new URL(avatarUrl);
      const isDev = isLocalhost();
      const authLocalBase = getServiceUrl('auth');
      const authProdHost = 'auth.airclou.com';

      // If running locally but URL points to production auth, rewrite to local auth
      if (isDev && u.hostname === authProdHost) {
        const localBase = new URL(authLocalBase);
        return `${localBase.origin}${u.pathname}${u.search}`;
      }

      // Otherwise, return as-is
      return avatarUrl;
    } catch {
      return avatarUrl;
    }
  }

  // Relative URL: prefix with auth service base
  if (avatarUrl.startsWith('/')) {
    const authBaseUrl = getServiceUrl('auth');
    return `${authBaseUrl}${avatarUrl}`;
  }

  return avatarUrl;
}

/**
 * Normalize a cover image URL to a full URL that can be accessed from any service
 * 
 * - If the URL is already absolute (starts with http/https), return as-is
 * - If the URL is relative (starts with /), prepend the playbook service URL
 * - If the URL is empty/null, return empty string
 * 
 * Database URLs should be stored WITHOUT the /playbook prefix (e.g., '/files/...' not '/playbook/files/...')
 * This function will add the correct base URL based on environment.
 * 
 * @param coverImageUrl - The cover image URL from the API (can be relative or absolute)
 * @returns A full URL that points to the playbook service
 * 
 * @example
 * ```ts
 * normalizeCoverImageUrl('/files/covers/cover-123.jpg')
 * // Returns 'http://localhost:3000/files/covers/cover-123.jpg' (in dev)
 * // Returns 'https://app.airclou.com/playbook/files/covers/cover-123.jpg' (in prod)
 * 
 * normalizeCoverImageUrl('/template-images/tpl_sales.jpg')
 * // Returns 'http://localhost:3000/template-images/tpl_sales.jpg' (in dev)
 * // Returns 'https://app.airclou.com/playbook/template-images/tpl_sales.jpg' (in prod)
 * 
 * normalizeCoverImageUrl('')
 * // Returns ''
 * ```
 */
export function normalizeCoverImageUrl(coverImageUrl: string | null | undefined): string {
  if (!coverImageUrl) return '';

  // Absolute URL: return as-is
  if (coverImageUrl.startsWith('http://') || coverImageUrl.startsWith('https://')) {
    return coverImageUrl;
  }

  // Relative URL starting with /
  if (coverImageUrl.startsWith('/')) {
    // Template images are static assets served by the frontend, not the backend
    // They should be accessed via the frontend URL
    if (coverImageUrl.startsWith('/playbook/template-images/') || coverImageUrl.startsWith('/template-images/')) {
      // In development (BASE_URL='/'), template images are at /template-images/
      // In production (BASE_URL='/playbook/'), template images are at /playbook/template-images/
      if (isLocalhost()) {
        // Strip /playbook/ prefix if it exists for development
        const path = coverImageUrl.startsWith('/playbook/') 
          ? coverImageUrl.substring(9) // Remove '/playbook'
          : coverImageUrl;
        return `http://localhost:5173${path}`;
      }
      
      // In production, ensure /playbook/ prefix exists
      const normalizedPath = coverImageUrl.startsWith('/template-images/')
        ? `/playbook${coverImageUrl}`
        : coverImageUrl;
      return normalizedPath;
    }
    
    // For other relative URLs (uploaded files), use the playbook backend service
    const playbookBaseUrl = getServiceUrl('playbook');
    return `${playbookBaseUrl}${coverImageUrl}`;
  }

  return coverImageUrl;
}
